/**
 * Lead Service
 * Guarda los leads de la landing en Supabase (tabla shared.landing_leads)
 * vía el edge function `capture-lead`. Fuente de verdad única de leads —
 * sin CRM externo. (Antes se llamaba hubspotService; HubSpot quedó descartado.)
 */

import { supabase } from '@/integrations/supabase/client'
import { getCaptcha } from '@/lib/captcha'

// Tipos para formularios B2C (Jams)
export interface LeadB2C {
  email: string
  firstname: string
  instrument: string
  level: string
  city?: string
  source: string
  /** Consentimiento explícito de marketing (RGPD) */
  marketing_consent?: boolean
}

// Tipos para formularios B2B (Escuelas, Salas)
export interface LeadB2B {
  email: string
  firstname: string
  phone?: string
  company: string
  city: string
  source: string
  /** Consentimiento explícito de marketing (RGPD) */
  marketing_consent?: boolean
}

export interface LeadResult {
  success: boolean
  error?: string
  id?: string
}

async function invokeCaptureLead(type: 'b2c' | 'b2b', data: Record<string, unknown>): Promise<LeadResult> {
  try {
    const captcha = await getCaptcha('capture_lead')
    const { data: result, error } = await supabase.functions.invoke('capture-lead', {
      body: { type, data, captcha_token: captcha.token, captcha_provider: captcha.provider },
    })
    if (error) return { success: false, error: 'Ha ocurrido un error. Inténtalo de nuevo.' }
    const parsed = result as LeadResult | null
    // A success UI is only valid once the API confirms that it persisted a lead.
    if (!parsed?.success || !parsed.id) return { success: false, error: parsed?.error || 'No pudimos guardar tu solicitud. Inténtalo de nuevo.' }
    return parsed
  } catch (error) {
    console.error('capture-lead error:', error)
    return { success: false, error: error instanceof Error ? error.message : 'No pudimos conectar. Revisa tu conexión e inténtalo de nuevo.' }
  }
}

// Tipos para captura inicial (solo email)
export interface LeadEmailOnly {
  email: string
  source: string
  /** Opt-in de comunicaciones comerciales, distinto del aviso de la beta. */
  marketing_consent?: boolean
}

/**
 * Envía solo el email (paso 1 del flujo de 2 pasos)
 * Crea el lead con email + source, luego se actualiza con más datos
 */
export async function submitEmailOnly(data: LeadEmailOnly): Promise<LeadResult> {
  return invokeCaptureLead('b2c', {
    email: data.email,
    lead_source: data.source,
    marketing_consent: data.marketing_consent ?? false,
  })
}

/**
 * Envía lead B2C (Jams - músicos)
 * Campos: email, instrumento, nivel, ciudad, source
 */
export async function submitLeadB2C(data: LeadB2C): Promise<LeadResult> {
  return invokeCaptureLead('b2c', {
    email: data.email,
    firstname: data.firstname,
    instrument: data.instrument,
    musical_level: data.level,
    city: data.city,
    lead_source: data.source,
    marketing_consent: data.marketing_consent ?? false,
  })
}

/**
 * Envía lead B2B (Escuelas, Salas)
 * Campos: email, nombre, teléfono, empresa, ciudad, source
 */
export async function submitLeadB2B(data: LeadB2B): Promise<LeadResult> {
  return invokeCaptureLead('b2b', {
    email: data.email,
    firstname: data.firstname,
    phone: data.phone,
    company: data.company,
    city: data.city,
    lead_source: data.source,
    marketing_consent: data.marketing_consent ?? false,
  })
}
